import { ChildSpecType } from 'api/schemas/childSpecType';
import type { Operand } from 'api/schemas/operand';
import { addError } from 'monitor';

type SpecificationWithHierarchy = {
  id: number;
  name: string;
  operands: Operand[];
  parent_spec_id?: number | null;
  child_spec_type?: ChildSpecType | null;
};

type ChildSpecificationKind = NonNullable<SpecificationWithHierarchy['child_spec_type']> | null;

export const isRootSpecification = <T extends Pick<SpecificationWithHierarchy, 'parent_spec_id'>>(specification: T) =>
  specification.parent_spec_id == null;

export const getChildSpecificationKind = <T extends SpecificationWithHierarchy>({
  childSpecification,
  reportInvalid = false,
}: {
  childSpecification: T;
  reportInvalid?: boolean;
}): ChildSpecificationKind => {
  if (isRootSpecification(childSpecification)) {
    return null;
  }

  if (childSpecification.child_spec_type === ChildSpecType.direct_child) {
    if (childSpecification.operands.length === 1) {
      return ChildSpecType.direct_child;
    }

    if (reportInvalid) {
      addError(new Error('Direct child specification must contain exactly one operand.'), {
        childSpecification,
      });
    }

    return null;
  }

  if (childSpecification.child_spec_type === ChildSpecType.sub_group) {
    return ChildSpecType.sub_group;
  }

  if (reportInvalid) {
    addError(new Error('Child specification is missing child_spec_type.'), {
      childSpecification,
    });
  }

  return null;
};

export const isDirectChildSpecification = <T extends SpecificationWithHierarchy>({
  childSpecification,
}: {
  childSpecification: T;
}) => getChildSpecificationKind({ childSpecification }) === ChildSpecType.direct_child;

export const isSubGroupSpecification = <T extends SpecificationWithHierarchy>({
  childSpecification,
}: {
  childSpecification: T;
}) => getChildSpecificationKind({ childSpecification }) === ChildSpecType.sub_group;

export const isOperandLevelChildSpecification = isDirectChildSpecification;

export const groupSubGroupSpecificationsByParentId = <T extends SpecificationWithHierarchy>(
  specifications: T[]
): Map<number, T[]> => {
  const specificationsById = new Map(specifications.map((specification) => [specification.id, specification]));
  const subGroupSpecificationsByParentId = new Map<number, T[]>();

  specifications.forEach((specification) => {
    if (isRootSpecification(specification)) {
      return;
    }

    const parentSpecificationId = specification.parent_spec_id;
    if (parentSpecificationId == null) {
      return;
    }

    const parentSpecification = specificationsById.get(parentSpecificationId);
    if (!parentSpecification) {
      return;
    }

    const childSpecificationKind = getChildSpecificationKind({
      childSpecification: specification,
      reportInvalid: true,
    });
    if (childSpecificationKind !== ChildSpecType.sub_group) {
      return;
    }

    let currentSubGroupSpecifications = subGroupSpecificationsByParentId.get(parentSpecification.id);
    if (!currentSubGroupSpecifications) {
      currentSubGroupSpecifications = [];
      subGroupSpecificationsByParentId.set(parentSpecification.id, currentSubGroupSpecifications);
    }
    currentSubGroupSpecifications.push(specification);
  });

  return subGroupSpecificationsByParentId;
};

export const buildSpecificationWithSubGroupOperands = <T extends SpecificationWithHierarchy>({
  specification,
  subGroupSpecificationsByParentId,
}: {
  specification: T;
  subGroupSpecificationsByParentId: Map<number, T[]>;
}): T => {
  const subGroupSpecifications = subGroupSpecificationsByParentId.get(specification.id) ?? [];
  if (subGroupSpecifications.length === 0) {
    return specification;
  }

  const mergedOperands = Array.from(
    new Set<Operand>([
      ...specification.operands,
      ...subGroupSpecifications.flatMap((subGroupSpecification) => subGroupSpecification.operands),
    ])
  );

  return {
    ...specification,
    operands: mergedOperands,
  };
};
